/**
 * THE LAST GATE: nothing the market has not traded may be drawn as though it had.
 *
 * <h2>Why this is a sweep and not a rule each overlay follows</h2>
 *
 * It was a rule each overlay followed, and that is why it failed. Forward marks were fixed in
 * `gannConfluenceOverlay`, and green and red triangles kept appearing, because the ones on screen
 * came from the candlestick-pattern predictions inside `EChartComponent` — which push their own
 * `markPoint` data straight onto the candlestick series and never pass through
 * `chartDataProcessor` at all.
 *
 * <p>There are seven producers of a triangle across five files, reaching ECharts by two different
 * routes. A convention that must be remembered in seven places is not a guarantee; it is a list of
 * places where the next one gets forgotten. So it is enforced ONCE, over the finished option,
 * after every producer has had its say.
 *
 * <h2>THE BOUNDARY IS DERIVED FROM THE BARS, NOT FROM A DATE PROP</h2>
 *
 * The previous version of this file asked the caller for a `lastRealDate` and compared each
 * marker's date against it. That failed for two independent reasons, and both are worth keeping
 * written down because each would have been enough on its own:
 *
 * <ol>
 *   <li><b>The markers have no dates.</b> A `markPoint` carries `coord: [index, y]` — a physical
 *       column number. Date matching only ever worked for the producers that happened to emit a
 *       date, which were not the ones drawing the triangles.</li>
 *   <li><b>In simulation mode the date prop was a lie.</b> `App` masks the future by REWRITING the
 *       prices (`maskBarsAfter`) while keeping the series' full length and dates. So the last date
 *       of `barData` is the end of all history, not the replay boundary, and a comparison against
 *       it classified nothing as future — on the one screen where the whole point is that the
 *       future is hidden.</li>
 * </ol>
 *
 * The boundary is therefore read from the rendered data itself and from the replay state, in
 * physical column indices — the same coordinate space the markers are actually expressed in.
 *
 * <h2>The two rules</h2>
 *
 * <pre>
 *   1. FLAT TAIL   padding writes the last real close into all four of O/H/L/C, so the projection
 *                  columns are a run of identical flat bars at the END of the series
 *   2. REPLAY      in simulation mode the caller supplies the reveal index; anything strictly
 *                  after it is hidden future
 * </pre>
 *
 * Either is sufficient. When both apply the EARLIER boundary wins, because a marker past either
 * one is a marker on a bar the reader cannot see.
 */

import { PENDING_COLOR, PENDING_SIGNALS_OVERLAY_ID, goldenCircleSize } from './pendingSignals';
// `chartRail` is a leaf module importing nothing, so this cannot cycle -- which is why the two
// rail constants live there rather than in the renderer that first needed them.
import { RAIL_Y_AXIS_INDEX } from './chartRail';

/**
 * The one colour for "this has not happened yet" — now actually shared, not merely described as it.
 *
 * <p>It was its own `'#d4a017'` literal directly under a comment saying it was "shared with the
 * pending-signal overlay". Three files held that same literal, each claiming the same thing, and
 * the claim was true only for as long as nobody edited one of them. Brightening the colour in one
 * place would have left this final gate repainting every forward marker back to the old gold — and
 * this gate runs LAST, so it would have won.
 */
export const FUTURE_MARKER_COLOR = PENDING_COLOR;

/**
 * The one overlay this gate does not touch.
 *
 * <p>Imported rather than written out, so the exemption cannot drift from the id it exempts.
 */
export const SCAN_OVERLAY_ID = PENDING_SIGNALS_OVERLAY_ID;

const isNum = v => typeof v === 'number' && Number.isFinite(v);

/** All four prices identical — what `extendBarData` and `maskBarsAfter` both write. */
export function isFlatBar(bar) {
  return Array.isArray(bar) && bar.length >= 4 && isNum(bar[0])
    && bar[0] === bar[1] && bar[1] === bar[2] && bar[2] === bar[3];
}

/**
 * Where the trailing run of padding begins.
 *
 * <h2>The TAIL, and at one identical price — not "the first flat bar"</h2>
 *
 * A real trading day can genuinely print open = high = low = close: a limit-locked move, or a
 * thinly traded name. Treating flatness on its own as the marker would put the boundary somewhere
 * inside real history on exactly those charts, and silently restyle months of legitimate marks.
 * `simulationMode.firstMaskedIndex` carries the same warning for the same reason.
 *
 * <p>Two conditions narrow it to padding specifically:
 *
 * <ul>
 *   <li>the run must reach the END of the series — an isolated flat day mid-history is not in it;
 *   <li>every bar in the run must carry the SAME price — padding is one close repeated, whereas
 *       two limit-locked days in a row would sit at two different prices.
 * </ul>
 *
 * <p>A series that is flat all the way through is rejected outright: that is a degenerate or
 * constant instrument, not a padded one, and treating every bar as future would blank the chart.
 *
 * <p><b>Known limit:</b> if the last REAL bar is itself flat at exactly the padding price, it is
 * indistinguishable from padding and is counted as future. The cost is one bar, on an instrument
 * whose final session was limit-locked at its own close — accepted as the better failure than
 * missing the boundary entirely.
 *
 * @returns {number} the first padded index, or Infinity when there is no padding
 */
export function flatTailStartIndex(ohlc) {
  if (!Array.isArray(ohlc) || ohlc.length === 0) return Infinity;

  const last = ohlc[ohlc.length - 1];
  if (!isFlatBar(last)) return Infinity;          // the series ends on a real bar

  const price = last[0];
  let i = ohlc.length - 1;
  while (i >= 0 && isFlatBar(ohlc[i]) && ohlc[i][0] === price) i -= 1;
  const start = i + 1;

  // The whole series is one flat price. Not padding — a constant or broken instrument.
  if (start === 0) return Infinity;
  return start;
}

/**
 * The first column index that counts as future, from both rules at once.
 *
 * @param ohlc                the RENDERED bar array, padding included
 * @param simulationBoundary  the replay reveal index, or null/undefined when not replaying.
 *                            Strictly greater is future, so the boundary bar itself is visible.
 * @returns {number} the first future index, or Infinity when nothing is future
 */
export function futureBoundaryIndex(ohlc, simulationBoundary) {
  const padded = flatTailStartIndex(ohlc);
  const replay = isNum(simulationBoundary) && simulationBoundary >= 0
    ? simulationBoundary + 1                      // strictly AFTER the revealed bar
    : Infinity;
  // The earlier of the two. A marker past either boundary sits on a bar the reader cannot see.
  return Math.min(padded, replay);
}

/** `yyyy-MM-dd`, which sorts lexicographically — so a string compare is a date compare. */
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The physical column a marker sits on, whatever shape its producer chose.
 *
 * <p>Six shapes are in live use, which is itself the argument for resolving them in one place:
 * `coord: [index, y]` on markPoints, `coord: [date, y]` where the axis is by value, `xAxis: index`
 * on markLines, `value: [index, y]` on scatter data, and `{date}` / `{name}` on overlay points.
 *
 * @returns {number} the index, or -1 when it genuinely cannot be determined
 */
export function markerIndex(point, dates = []) {
  if (!point || typeof point !== 'object') return -1;
  const fromDate = (d) => {
    const i = dates.indexOf(d);
    return i >= 0 ? i : -1;
  };

  const coord = Array.isArray(point.coord) ? point.coord[0] : undefined;
  if (isNum(coord)) return Math.round(coord);
  if (DATE_RE.test(coord)) return fromDate(coord);

  if (isNum(point.xAxis)) return Math.round(point.xAxis);
  if (DATE_RE.test(point.xAxis)) return fromDate(point.xAxis);

  const v = Array.isArray(point.value) ? point.value[0] : undefined;
  if (isNum(v)) return Math.round(v);
  if (DATE_RE.test(v)) return fromDate(v);

  if (DATE_RE.test(point.date)) return fromDate(point.date);
  if (DATE_RE.test(point.name)) return fromDate(point.name);

  return -1;
}

/**
 * Placements that mean the market has not traded this column.
 *
 * <p>Retained ALONGSIDE the index rules, not instead of them. It is strictly extra information
 * that costs nothing and cannot produce a false positive — it fires only where a producer has
 * explicitly declared the point forward. It also catches the one case no index rule can: a pending
 * signal marked OFF_CHART has no column at all, so it has no index to compare.
 *
 * <p>Matched case-INSENSITIVELY: our overlays emit lowercase, a caller reading the backend enum
 * would write uppercase, and a guard that fails silently on a difference of case is the same class
 * of defect it exists to prevent.
 */
const FUTURE_PLACEMENTS = new Set(['extended', 'missing', 'offchart', 'off_chart']);

const isFuturePlacement = placement => typeof placement === 'string'
  && FUTURE_PLACEMENTS.has(placement.trim().toLowerCase());

/**
 * Is this marker on a column the reader cannot see?
 *
 * <p>Unknown is NOT future. A marker whose column cannot be resolved and which declares no
 * placement is left exactly as its producer drew it — restyling on suspicion would corrupt
 * historical marks across every study in the product, which is a far worse failure than the one
 * being fixed.
 */
export function isFutureMarker(point, { boundary = Infinity, dates = [] } = {}) {
  if (!point || typeof point !== 'object') return false;
  if (isFuturePlacement(point.placement)) return true;
  if (!Number.isFinite(boundary)) return false;
  const idx = markerIndex(point, dates);
  return idx >= 0 && idx >= boundary;
}

/**
 * Strip the trade styling from one marker, in place.
 *
 * <p>Both rotation keys are removed. `symbolRotate` is what ECharts reads; `rotation` is what some
 * producers write and what the bug report named. Removing only the one that is read would leave
 * the other for a refactor that "tidied" the naming to pick up again.
 */
export function forceFutureMarkerPoint(point) {
  if (!point || typeof point !== 'object') return point;

  // ALREADY A GOLDEN CIRCLE? Then its size has already been decided - by a renderer that shrank
  // it, or by an earlier run of this gate - and shrinking it AGAIN is not idempotent. Read before
  // anything is mutated, because the very next lines are what would make it look already-forced.
  //
  // This gate runs over a finished option and an option object survives re-renders, so "twice" is
  // ordinary rather than exotic: 18 -> 14 -> 11 would walk a CTS mark straight back under the
  // width its index digit needs. The sibling idempotency test is what caught it.
  const alreadyGolden = point.symbol === 'circle'
    && point.itemStyle && point.itemStyle.color === FUTURE_MARKER_COLOR;

  delete point.symbolRotate;
  delete point.rotation;
  point.symbol = 'circle';
  // THE SIZE, WHICH THIS GATE USED TO LEAVE ALONE — and leaving it alone is why a 20% reduction
  // applied at the renderers changed nothing on screen. This gate runs LAST, and it is the path
  // that actually produces the gold circles a reader sees: the markers arriving here were built by
  // a different producer entirely - the markPoint data EChartComponent pushes onto the candlestick
  // series - which never passed through the renderers that were taught to shrink.
  //
  // Forced alongside the shape and the colour, for the reason this whole function exists: three
  // properties that must agree, decided in one place, so none can be right while another is wrong.
  if (!alreadyGolden && Number.isFinite(point.symbolSize)) {
    point.symbolSize = goldenCircleSize(point.symbolSize);
  }
  point.itemStyle = { ...(point.itemStyle || {}), color: FUTURE_MARKER_COLOR };

  // A gold circle with a green label is still a green mark. The colour channel has to be taken
  // whole or the override is cosmetic — the reader sees the same bull/bear claim, in text.
  if (point.label && typeof point.label === 'object') {
    if (point.label.color) point.label.color = FUTURE_MARKER_COLOR;
    if (point.label.borderColor) point.label.borderColor = FUTURE_MARKER_COLOR;
  }
  if (point.emphasis?.itemStyle) {
    point.emphasis.itemStyle = { ...point.emphasis.itemStyle, color: FUTURE_MARKER_COLOR };
  }
  return point;
}

/**
 * Sweep a finished ECharts option and force every future marker to a gold circle.
 *
 * <p>Call this LAST, immediately before the option reaches the chart, so it sits after every
 * producer regardless of the route that producer took.
 *
 * @param option              the finished option — mutated in place and returned
 * @param ohlc                the RENDERED bar array, padding included, for the flat-tail rule
 * @param dates               the rendered category axis, to resolve a date-shaped coord
 * @param simulationBoundary  the replay reveal index, or null when not replaying
 * @returns `{option, changed, boundary}` — `changed` is the count and `boundary` the index used,
 *          so a caller can tell a sweep that matched nothing from one that never ran. Telling
 *          those two apart from a screenshot is exactly what cost the previous attempt.
 */
export function forceFutureMarkerStyle(
  option, { ohlc = [], dates = [], simulationBoundary = null } = {},
) {
  const boundary = futureBoundaryIndex(ohlc, simulationBoundary);
  let changed = 0;
  if (!option || !Array.isArray(option.series)) return { option, changed, boundary };

  const ctx = { boundary, dates };
  const sweep = (arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((p) => {
      if (isFutureMarker(p, ctx)) {
        forceFutureMarkerPoint(p);
        changed += 1;
      }
    });
  };

  option.series.forEach((s) => {
    if (!s || typeof s !== 'object') return;
    // EXEMPT: the daily-scan markers.
    //
    // Everything else forward is shapeless on purpose, because its direction is not known. A scan
    // row's direction IS the product - it is a resolved LONG or SHORT - so it is drawn as an
    // up or down arrow and must keep that through this sweep. Keyed on the SERIES ID rather than
    // on the shape it currently uses: keying on 'triangle' would re-arm the override the moment
    // somebody changed the glyph, which is exactly the class of silent regression this file exists
    // to prevent.
    if (typeof s.id === 'string' && s.id.includes(SCAN_OVERLAY_ID)) return;
    // EXEMPT: anything drawn on the RAIL.
    //
    // This gate's premise is that nothing untraded may be drawn AS THOUGH IT HAD. A mark on the
    // rail has already satisfied that, by construction and more completely than this sweep can:
    // it is pinned to a 0..1 axis at the foot of the grid, so it asserts no price, and the
    // renderer has already stripped its rotation and painted it the neutral fill. Forcing it here
    // would take a mark that correctly says "a date, nothing more" and repaint it as a pending
    // SIGNAL, which is a different claim.
    //
    // Keyed on the AXIS rather than on an id, and that is deliberately stronger than the
    // exemption above: an id is a name somebody can forget to add, where being on the rail IS
    // the property this exemption is about. Any overlay that later adopts the rail inherits it
    // without a second edit -- which is the failure mode this whole file was written against.
    if (s.yAxisIndex === RAIL_Y_AXIS_INDEX) return;
    // The candlestick series' own data is OHLC ARRAYS, not styleable objects, and must not be
    // touched — isFutureMarker rejects non-objects, but the intent is worth stating.
    sweep(s.data);
    sweep(s.markPoint?.data);
    // markLine is deliberately absent: those are the dashed vertical prediction lines, which carry
    // no symbol and no rotation. Recolouring them would erase a bull/bear reading nobody asked to
    // lose, and they are not the triangles this gate is about.
  });

  return { option, changed, boundary };
}

export default forceFutureMarkerStyle;
